import {catchError, from, map, Observable, switchMap, tap} from 'rxjs';
import {AxiosError, AxiosResponse} from 'axios';
import {UIEntitiesRef, updateAllEntities, upsertEntities, upsertEntitiesById} from '@ngneat/elf-entities';
import {setPage, updatePaginationData} from '@ngneat/elf-pagination';
import {GridSortItem} from '@mui/x-data-grid-pro';
import fileDownload from 'js-file-download';

import {Pagination} from '@store/common/pagination.model';
import {suppliersServicesStore} from '@store/suppliers/services/suppliers-services.store';
import {
  ManageSuppliersService,
  SuppliersService,
  SuppliersServiceDetails,
  SuppliersServicesFilters,
  SuppliersServicesPagination
} from '@store/suppliers/services/suppliers-services.model';
import {getSuppliersServicesDataSource} from '@store/suppliers/services/suppliers-services.requests';
import {TableFilter} from '@store/common/tableFilter.model';

import APIAxios, {APIRoutes} from '@api/axios.api';
import SnackError from '@utils/error.utils';

export class SuppliersServicesService {
  store = suppliersServicesStore;

  resetStore = () => this.store.reset();

  setSort = (sort: GridSortItem) => this.store.update((state) => ({...state, sort}));
  setPagination = (name: string) => (value: number) => this.store.update((state) => ({
    ...state,
    tablePagination: {
      ...state.tablePagination,
      [name]: value,
    },
  }));

  resetFilters = (supplierId?: string) => this.store.update((state) => ({
    ...state,
    filters: {search: '', tableFilters: SuppliersServicesFilters.initTableFilters(supplierId)},
  }));
  setFilters = (name: string) => (value: any) => this.store.update((state) => ({
    ...state,
    filters: {
      ...state.filters,
      [name]: value,
    },
  }));

  getSuppliersServices = (): Observable<SuppliersServicesPagination> => {
    const {filters: {search, tableFilters}, sort, tablePagination} = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETSuppliersServices(),
      params: {
        ...requestFilters,
        searchString: search || undefined,
        page: tablePagination.page,
        limit: tablePagination.limit,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<SuppliersServicesPagination>) => {
          return response.data;
        }),
        tap((suppliersServicesPagination) => {
          const paginationData = Pagination.toPaginationData(suppliersServicesPagination, tablePagination.page);

          return this.store.update(
            upsertEntities(suppliersServicesPagination.data),
            upsertEntitiesById(suppliersServicesPagination.data.map((supplierService) => supplierService.id), {
              updater: (supplierServiceUi) => supplierServiceUi,
              creator: (supplierServiceId) => ({id: supplierServiceId, selected: false}),
              ref: UIEntitiesRef,
            }),
            updatePaginationData(paginationData),
            setPage(paginationData.currentPage, suppliersServicesPagination.data.map((suppliersService) => suppliersService.id)),
            getSuppliersServicesDataSource.setSuccess(),
          );
        }),
        getSuppliersServicesDataSource.trackRequestStatus(),
      );
  };

  getSuppliersServicesCSV = (): Observable<Blob> => {
    const {filters: {search, tableFilters}, sort} = this.store.getValue();
    const requestFilters = TableFilter.toRequestFilters(tableFilters);

    return from(APIAxios({
      ...APIRoutes.GETSuppliersServicesCSV(),
      params: {
        ...requestFilters,
        disablePaginate: true,
        searchString: search || undefined,
        sortBy: sort.field,
        orderBy: sort.sort?.toLocaleUpperCase(),
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<Blob>) => {
          return response.data;
        }),
        tap((data) => {
          const date = new Date();
          const formattedDate =
            date.getDate() + '_' + (date.getMonth() + 1) + '_' + date.getFullYear();
          fileDownload(data, `supplier${!requestFilters.supplierOrganisationIds && !requestFilters.supplierOrganisationUnitIds ? 's' : ''}${!requestFilters.supplierOrganisationUnitIds ? '_unit' : ''}_services_${formattedDate}.csv`);
        }),
      );
  };

  createSuppliersService = (supplierUnitId: string | number, data: ManageSuppliersService): Observable<SuppliersService> => {
    return from(APIAxios({
      ...APIRoutes.POSTSuppliersService(supplierUnitId),
      data: {
        ...data,
        serviceKindId: data.serviceKind?.id || undefined,
        wasteId: data.waste?.value || undefined,
        containerId: data.container?.value || undefined,
        availabilities: data.availabilities?.length ? data.availabilities.map((a) => ({
          ...a,
          availableFrom: a.availableFrom?.toISOString(),
          availableTo: a.availableTo?.toISOString(),
          availableZipCodes: a.availableZipCodes || [],
        })) : [],
        prices: data.prices?.length ? data.prices.map((p) => ({
          unitCostInCents: (p.unitCost || 0) * 100,
          minQuantity: p.minQuantity,
          maxQuantity: p.maxQuantity,
        })) : [],
      },
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<SuppliersService>) => {
          return response.data;
        }),
        switchMap((service) => this.getSuppliersServices().pipe(map(() => service))),
      );
  };

  updateSupplierService(serviceId: number, service: ManageSuppliersService) {
    return from(
      APIAxios({
        ...APIRoutes.PATCHSupplierService(serviceId),
        data: {
          supplierName: service.supplierName,
          buyPricesToAdd: service.prices?.filter((p) => p.id == null),
          buyPricesToUpdate: service.prices?.filter((p) => p.wasUpdated === true),
        }
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<SuppliersService>) => {
        return response.data;
      }),
      switchMap((service) => this.getSuppliersServices().pipe(map(() => service))),
    );
  }

  getSupplierServiceById = (serviceId: number | string): Observable<ManageSuppliersService> => {
    return from(APIAxios(APIRoutes.GETSupplierServiceById(serviceId)))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<SuppliersServiceDetails>) => {
          return SuppliersServiceDetails.toManageSuppliersService(response.data);
        }),
      );
  };

  // deleteSuppliersServiceInList = (suppliersServiceId: number | string, supplierId?: number | string): Observable<SuppliersServicesPagination> => {
  //   return this.getSuppliersServices();
  //   // return from(APIAxios(APIRoutes.DELETESuppliersUnit(suppliersUnitId)))
  //   //   .pipe(
  //   //     catchError((err: AxiosError) => {
  //   //       throw new SnackError((err.response as any)?.data?.message, "error");
  //   //     }),
  //   //     switchMap(() => this.getSuppliersUnits(this.store.getValue().pagination.currentPage, this.store.getValue().search, supplierId)),
  //   //   );
  // };

  setSelectedSuppliersServices = (suppliersServiceIds: number[]) => {
    this.store.update(
      updateAllEntities(
        (suppliersServiceUI) => ({
          ...suppliersServiceUI,
          selected: suppliersServiceIds.includes(suppliersServiceUI.id)
        }),
        {ref: UIEntitiesRef},
      ),
    );
  };


}

export const suppliersServicesService = new SuppliersServicesService();
